import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { api } from '../api/client';

const ProductPricingV2 = forwardRef(({ productData, onUpdate }, ref) => {
  // State for simple 6-field pricing
  const [wholesalePrice, setWholesalePrice] = useState(0);
  const [retailPrice, setRetailPrice] = useState(0);
  const [manufacturingCost, setManufacturingCost] = useState(0);
  const [importShippingCost, setImportShippingCost] = useState(0);
  const [fulfillmentCost, setFulfillmentCost] = useState(0);
  const [marketingPercent, setMarketingPercent] = useState(15);
  
  const [showDetails, setShowDetails] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Helper function for handling number inputs - allows smooth typing
  const handleNumberInput = (setter) => (e) => {
    const value = e.target.value;
    
    // Allow empty string (user clearing field)
    if (value === '') {
      setter(0);
      return;
    }
    
    // Parse the number
    const numValue = parseFloat(value);
    
    // Only update if it's a valid number
    if (!isNaN(numValue)) {
      setter(numValue);
    }
  };
  
  // Load data
  useEffect(() => {
    if (productData?.pricing_v2) {
      console.log('📥 Loading Pricing v2 data:', productData.pricing_v2);
      setWholesalePrice(productData.pricing_v2.wholesale_price || 0);
      setRetailPrice(productData.pricing_v2.retail_price || 0);
      setManufacturingCost(productData.pricing_v2.manufacturing_cost || 0);
      setImportShippingCost(productData.pricing_v2.import_shipping_cost || 0);
      setFulfillmentCost(productData.pricing_v2.fulfillment_cost || 0);
      setMarketingPercent(productData.pricing_v2.marketing_percent || 15);
      setHasUnsavedChanges(false);
    }
  }, [productData]);
  
  // Track changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [wholesalePrice, retailPrice, manufacturingCost, importShippingCost, fulfillmentCost, marketingPercent]);
  
  // Calculations
  const marketingCost = (marketingPercent / 100) * retailPrice;
  
  // For Retail: Include all costs (freight paid by us)
  const totalCost = manufacturingCost + importShippingCost + fulfillmentCost + marketingCost;
  
  // For Wholesale: Exclude fulfillment cost (freight paid by customer)
  const wholesaleCost = manufacturingCost + importShippingCost + marketingCost;
  
  const wholesaleProfit = wholesalePrice - wholesaleCost;
  const retailProfit = retailPrice - totalCost;
  const wholesaleMargin = wholesalePrice > 0 ? ((wholesaleProfit / wholesalePrice) * 100) : 0;
  const retailMargin = retailPrice > 0 ? ((retailProfit / retailPrice) * 100) : 0;
  
  // Check if detailed data exists
  const hasDetailedData = productData?.pricing_v2?.raw_materials_cost > 0 || 
                          productData?.pricing_v2?.labor_cost > 0;
  
  // Expose handleSave to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      console.log('💾 Pricing v2 handleSave called');
      
      const pricingV2Data = {
        wholesale_price: wholesalePrice,
        retail_price: retailPrice,
        manufacturing_cost: manufacturingCost,
        import_shipping_cost: importShippingCost,
        fulfillment_cost: fulfillmentCost,
        marketing_percent: marketingPercent,
        total_cost: totalCost,  // Total cost for retail (includes fulfillment)
        wholesale_cost: wholesaleCost,  // Wholesale cost (excludes fulfillment)
        wholesale_margin: wholesaleMargin,
        retail_margin: retailMargin,
        // Calculated values
        marketing_cost: marketingCost,
        wholesale_profit: wholesaleProfit,
        retail_profit: retailProfit
      };
      
      console.log('📊 Pricing v2 data to save:', pricingV2Data);
      
      // Update parent state
      onUpdate('pricing_v2', pricingV2Data);
      
      // CRITICAL FIX: Save to backend API
      try {
        const tenantId = localStorage.getItem('tenant_id');
        
        if (!productData.id && !productData._id) {
          throw new Error('Product ID not found');
        }
        
        const productId = productData.id || productData._id;
        
        console.log('🌐 Saving pricing_v2 to backend...', {
          productId,
          tenantId,
          pricingV2Data
        });
        
        const response = await api.put(`/api/v1/product-master/item/${productId}`, {
          updates: {
            pricing_v2: pricingV2Data
          }
        }, {
          headers: {
            'X-Tenant-ID': tenantId
          }
        });
        
        if (response.status !== 200) {
          throw new Error(response.data.detail || 'Failed to save pricing data');
        }
        
        const result = response.data;
        console.log('✅ Pricing v2 saved to backend:', result);
        
        // Dispatch event to refresh Product Master list
        window.dispatchEvent(new CustomEvent('refreshProductList', {
          detail: { productId, updatedData: { pricing_v2: pricingV2Data } }
        }));
        
        window.dispatchEvent(new CustomEvent('productUpdated', {
          detail: { 
            id: productId,
            pricing_v2: pricingV2Data
          }
        }));
        
        setHasUnsavedChanges(false);
        
        // Show success toast
        const { toast } = await import('sonner');
        toast.success('Pricing saved successfully!');
        
        return true; // Indicate save was successful
      } catch (error) {
        console.error('❌ Failed to save pricing_v2:', error);
        
        // Show error toast
        const { toast } = await import('sonner');
        toast.error(`Failed to save: ${error.message}`);
        
        throw error; // Re-throw to let parent handle
      }
    },
    
    hasUnsavedChanges: hasUnsavedChanges
  }));
  
  // Update parent - debounced to avoid infinite loops
  useEffect(() => {
    const pricingData = {
      wholesale_price: wholesalePrice,
      retail_price: retailPrice,
      manufacturing_cost: manufacturingCost,
      import_shipping_cost: importShippingCost,
      fulfillment_cost: fulfillmentCost,
      marketing_percent: marketingPercent,
      total_cost: totalCost,
      wholesale_margin: wholesaleMargin,
      retail_margin: retailMargin
    };
    
    // Only update parent when values actually change (not on initial mount)
    const timer = setTimeout(() => {
      onUpdate('pricing_v2', pricingData);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [wholesalePrice, retailPrice, manufacturingCost, importShippingCost, fulfillmentCost, marketingPercent]);
  
  return (
    <div className="space-y-4">
      {/* HEADER - Compact */}
      <div className="bg-[#1a2744] rounded-lg p-3">
        <div className="flex items-center gap-2">
          <span className="text-white text-xs font-semibold uppercase tracking-wider">✨ Simplified Pricing (v2)</span>
        </div>
      </div>
      
      {/* SECTION 1: PRICING - 2x2 Grid */}
      <div className="bg-[#1a2744] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-white text-xs font-semibold uppercase tracking-wider">💰 PRICING</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">WHOLESALE</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={wholesalePrice}
                onChange={handleNumberInput(setWholesalePrice)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-7 pr-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">RETAIL</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={retailPrice}
                onChange={handleNumberInput(setRetailPrice)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-7 pr-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* SECTION 2: COSTS - 2x2 Grid */}
      <div className="bg-[#1a2744] rounded-lg p-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold uppercase tracking-wider">📦 COSTS</span>
          {hasDetailedData && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-[10px] text-blue-400 hover:text-blue-300"
            >
              {showDetails ? '▼ Hide' : '▶ Details'}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">MANUFACTURING</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={manufacturingCost}
                onChange={handleNumberInput(setManufacturingCost)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-7 pr-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">IMPORT & SHIPPING</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={importShippingCost}
                onChange={handleNumberInput(setImportShippingCost)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-7 pr-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">FULFILLMENT</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={fulfillmentCost}
                onChange={handleNumberInput(setFulfillmentCost)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-7 pr-3 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-[10px] uppercase tracking-wider">MARKETING</label>
            <div className="relative">
              <input
                type="number"
                value={marketingPercent}
                onChange={handleNumberInput(setMarketingPercent)}
                className="w-full bg-[#0d1b2a] text-white text-sm pl-3 pr-7 py-2 rounded border border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="15"
                step="0.1"
                min="0"
                max="100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
            </div>
            {retailPrice > 0 && marketingPercent > 0 && (
              <span className="text-[10px] text-blue-400 block mt-1">
                = ${marketingCost.toFixed(2)}/unit
              </span>
            )}
          </div>
        </div>
        
        {/* Detailed breakdown (if exists) - Compact */}
        {showDetails && hasDetailedData && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="grid grid-cols-3 gap-2 text-[10px]">
              <div className="text-gray-400">Raw: <span className="text-white">${(productData.pricing_v2?.raw_materials_cost || 0).toFixed(2)}</span></div>
              <div className="text-gray-400">Labor: <span className="text-white">${(productData.pricing_v2?.labor_cost || 0).toFixed(2)}</span></div>
              <div className="text-gray-400">Overhead: <span className="text-white">${(productData.pricing_v2?.factory_overhead || 0).toFixed(2)}</span></div>
            </div>
          </div>
        )}
      </div>
      
      {/* TOTAL COGS - Compact Summary */}
      <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-lg p-3 border border-blue-600">
        <div className="flex items-center justify-between">
          <span className="text-white text-xs font-semibold uppercase tracking-wider">💰 TOTAL COGS</span>
          <span className="text-white text-2xl font-bold">${totalCost.toFixed(2)}</span>
        </div>
        <div className="text-[10px] text-gray-400 mt-1">
          = ${manufacturingCost.toFixed(2)} + ${importShippingCost.toFixed(2)} + ${fulfillmentCost.toFixed(2)} + ${marketingCost.toFixed(2)}
        </div>
      </div>
      
      {/* SECTION 3: MARGINS - Side by Side */}
      <div className="bg-[#1a2744] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-white text-xs font-semibold uppercase tracking-wider">📊 MARGINS</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0d1b2a] rounded p-2 border border-gray-700">
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">Wholesale</span>
            <p className={`text-lg font-bold ${wholesaleMargin >= 30 ? 'text-green-400' : wholesaleMargin >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
              {wholesaleMargin.toFixed(1)}%
            </p>
            <span className="text-[10px] text-gray-500">${wholesaleProfit.toFixed(2)} profit</span>
          </div>
          <div className="bg-[#0d1b2a] rounded p-2 border border-gray-700">
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">Retail</span>
            <p className={`text-lg font-bold ${retailMargin >= 40 ? 'text-green-400' : retailMargin >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
              {retailMargin.toFixed(1)}%
            </p>
            <span className="text-[10px] text-gray-500">${retailProfit.toFixed(2)} profit</span>
          </div>
        </div>
      </div>
      
      {/* Pricing Tips - Collapsed by Default */}
      <details className="bg-[#1a2744] rounded-lg">
        <summary className="p-3 cursor-pointer text-gray-400 text-xs flex items-center justify-between">
          <span>💡 Pricing Tips</span>
          <span className="text-[10px]">▼</span>
        </summary>
        <div className="px-3 pb-3 text-[11px] text-gray-400 space-y-1">
          <p>• Wholesale margins: Aim for 25-35%</p>
          <p>• Retail margins: Aim for 40-50%</p>
          <p>• Marketing budget: Typically 10-20% of retail price</p>
          <p>• COGS (wholesale) excludes fulfillment (customer pays freight)</p>
        </div>
      </details>
    </div>
  );
});

ProductPricingV2.displayName = 'ProductPricingV2';

export default ProductPricingV2;
